import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { scrollToSection, requestLeadType } from "@/lib/scroll";

const HERO_IMG =
  "https://images.pexels.com/photos/36605869/pexels-photo-36605869.jpeg?auto=compress&cs=tinysrgb&w=1920";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 240]);
  const fade = useTransform(scrollY, [0, 550], [1, 0]);

  const goToForm = (type) => {
    requestLeadType(type);
    scrollToSection("#contact");
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Sun rays breaking through clouds"
          className="h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/50 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-ink/40" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2.2, delay: 0.5 }}
        className="pointer-events-none absolute -top-44 left-1/2 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-gold/15 blur-[150px]"
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1.2 }}
        className="absolute right-6 top-1/2 hidden -translate-y-1/2 lg:block"
      >
        <span
          data-testid="hero-scripture-side"
          className="text-[10px] uppercase tracking-[0.55em] text-white/40"
          style={{ writingMode: "vertical-rl" }}
        >
          Colossians 3:23
        </span>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-44 md:px-10"
      >
        <MaskedLine delay={0.35}>
          <span
            data-testid="hero-eyebrow"
            className="mb-7 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.4em] text-gold"
          >
            <span className="h-px w-12 bg-gold/80" />
            Built on Faith. Built to Last.
          </span>
        </MaskedLine>

        <h1 className="font-serif text-[13.5vw] font-medium leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.5}>
            <span className="text-white">Seraph Roofing</span>
          </MaskedLine>
          <MaskedLine delay={0.68}>
            <span className="italic text-gold">&mdash; Above the Rest.</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.9} className="mt-9 max-w-xl">
          <p data-testid="hero-subheadline" className="text-base text-white/85 md:text-lg">
            Built on faith. Driven by excellence. Protecting what matters most.
          </p>
        </MaskedLine>
        <MaskedLine delay={1.0} className="mt-3 max-w-xl">
          <p data-testid="hero-supporting-text" className="text-sm leading-relaxed text-white/55">
            Get a FREE roof, siding, or gutter inspection and a FREE quote from a team
            committed to honest service and quality workmanship.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-11 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-free-inspection-btn"
            onClick={() => goToForm("inspection")}
            className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-ink transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
          >
            Get My Free Inspection
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
          <button
            data-testid="hero-free-quote-btn"
            onClick={() => goToForm("quote")}
            className="inline-flex items-center gap-3 border border-white/25 px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-white transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-gold"
          >
            Get a Free Quote
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-white/10 pt-6"
        >
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/45">
            <span data-testid="hero-badge-inspections">Free Inspections</span>
            <span data-testid="hero-badge-storm">Storm Restoration</span>
            <span data-testid="hero-badge-retail">Retail &amp; Insurance</span>
          </div>
          <button
            data-testid="hero-scroll-cue"
            onClick={() => scrollToSection("#services")}
            className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-white/45 transition-colors duration-300 hover:text-gold"
          >
            Scroll
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            >
              <ArrowDown className="h-4 w-4 text-gold" />
            </motion.span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
